import { gql } from "@apollo/client";
import { message } from "antd";
import { noAuthClient } from "services/axios";
import { getAccessToken, getUser, logout } from "services/auth";
import { getGQLQueryData } from "entity-app/services";
import { CHECK_FEATURE_FIELD_VALUE_EXISTS } from "entity-app/graphQL/ciq-feature-queries";
import _ from "lodash";
import {
  CustomUserProjectRoles,
  ESubmittalMgmtModes,
  ESubmittalStatus,
  UserProjectRoles
} from "../constants";

function isEqualIgnoreArrayOrder(a: any, b: any): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    const sortedA = a
      .map((item) => (_.isPlainObject(item) ? _.sortBy(item) : item))
      .sort();
    const sortedB = b
      .map((item) => (_.isPlainObject(item) ? _.sortBy(item) : item))
      .sort();
    return _.isEqual(sortedA, sortedB);
  }
  if (_.isPlainObject(a) && _.isPlainObject(b)) {
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (!_.isEqual(aKeys, bKeys)) return false;
    return aKeys.every((key) => isEqualIgnoreArrayOrder(a[key], b[key]));
  }
  return _.isEqual(a, b);
}

export const compareObjectPropertyValues = (
  objectArr: any[],
  fieldsToCheck: { field: string; diffFound?: boolean }[]
): { diffFoundSummary: boolean; fieldsAsArray: any[]; fieldsAsObject: any } => {
  if (objectArr && objectArr.length === 1)
    return {
      diffFoundSummary: false,
      fieldsAsArray: fieldsToCheck,
      fieldsAsObject: {}
    };

  let diffFoundSummary = false;
  const fieldsAsObject: any = {};

  const newFieldsAsArray = fieldsToCheck.map((item: any) => {
    const diffFound = objectArr.some(
      (obj) =>
        !isEqualIgnoreArrayOrder(objectArr[0][item.field], obj[item.field])
    );
    fieldsAsObject[item.field] = { ...item, diffFound };
    if (diffFound) diffFoundSummary = true;
    return { ...item, diffFound };
  });

  return { diffFoundSummary, fieldsAsArray: newFieldsAsArray, fieldsAsObject };
};

export const downloadFileInBrowser = (
  url: string,
  fileName: string,
  cb?: () => void
) => {
  fetch(url)
    .then((resp) => resp.blob())
    .then((blob) => {
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = downloadUrl;
      // the filename you want
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      if (cb) cb();
    })
    .catch(() => {
      message.error("Downloading Failed");
      if (cb) cb();
    });
};

export const getUserCompany = (item: any) => {
  if (item?.company) return item?.company?.name;
  try {
    if (item?.subscription_vendor) return item.subscription_vendor.name;
    if (item?.subscription)
      return item?.subscription.organization_subscriptions?.[0]?.organization
        ?.name;
  } catch (ex) {
    return "";
  }
  return "";
};

export const getSubmittalStatus = (historyItems: any) => {
  if (!historyItems) return null;
  for (let h = 0; h < historyItems.length; h += 1) {
    if (historyItems[h].status) return historyItems[h].status;
  }
  return null;
};

export const matchUserProjectRole = (
  subscriptionRole: number,
  projectRole: string | undefined
) => {
  const roleList: any = UserProjectRoles[subscriptionRole];
  return roleList.includes(projectRole);
};

export const matchCustomUserProjectRole = (
  subscriptionRole: number,
  projectRole: string | undefined
) => {
  const roleList: any = CustomUserProjectRoles[subscriptionRole];
  return roleList.includes(projectRole);
};

export const padWithLeadingZeros = (num: number, totalLength: number) => {
  return String(num).padStart(totalLength, "0");
};

export const mergeArrays = (arrayA: any[], arrayB: any[], idField: string) => {
  const arrayAIds: string[] = [];
  const arrayBIds: string[] = [];

  for (let b = 0; b < arrayB.length; b += 1) {
    arrayBIds.push(arrayB[b][idField]);
  }

  const updatedArray: any = arrayA.map((objA: any) => {
    arrayAIds.push(objA[idField]);

    if (arrayBIds.includes(objA[idField])) {
      const objBIdx = arrayBIds.indexOf(objA[idField]);
      return { ...objA, ...arrayB[objBIdx] };
    }
    return objA;
  });

  for (let b = 0; b < arrayB.length; b += 1) {
    if (!arrayAIds.includes(arrayB[b][idField])) {
      updatedArray.push(arrayB[b]);
    }
  }
  // TODO: handle delete flags filtering later
  return updatedArray;
};

export const mergedStrings = (strArr: string[] = []) => {
  return strArr.filter((str) => str && str?.toString().length).join(" - ");
};

export const isProjectInIntegrationMode = (projectMode: number) => {
  if (!projectMode) return false;
  return projectMode === ESubmittalMgmtModes.INTEGRATION_MODE;
};

export const updateSubmittalParticipantsForINTG = (submittalDetails: any) => {
  if (!submittalDetails) return submittalDetails;

  const submittalParticipants = {
    assignee: "",
    submitter: "",
    submitter_user: null,
    gc_reviewer: "",
    gc_reviewer_user: {
      project_users: [
        {
          subscription: {
            organization_subscriptions: [
              {
                organization: {
                  name: ""
                }
              }
            ]
          }
        }
      ]
    },
    design_reviewer: "",
    design_reviewer_user: {
      project_users: [
        {
          subscription_vendor: {
            name: ""
          }
        }
      ]
    },
    responsible_contractor: "",
    responsible_contractor_vendor: {
      id: "",
      name: ""
    }
  };

  submittalDetails?.submittal_participation_from_integrations.forEach(
    (participant: any) => {
      const participantType: string =
        participant?.submittal_participation_type?.key;
      switch (participantType) {
        case "assignee":
          submittalParticipants.assignee =
            participant.project_integration_user_participant?.id;
          break;
        case "submitter":
          submittalParticipants.submitter =
            participant.project_integration_user_participant?.id;
          submittalParticipants.submitter_user = {
            ...participant.project_integration_user_participant,
            project_users: [
              {
                status_id: null
              }
            ]
          };
          break;
        case "gc_reviewer":
          submittalParticipants.gc_reviewer =
            participant.project_integration_user_participant?.id;
          submittalParticipants.gc_reviewer_user = {
            ...submittalParticipants.gc_reviewer_user,
            ...participant.project_integration_user_participant
          };
          submittalParticipants.gc_reviewer_user.project_users[0].subscription.organization_subscriptions[0].organization.name =
            participant.project_integration_user_participant?.project_integration_vendor?.name;
          break;
        case "design_reviewer":
          submittalParticipants.design_reviewer =
            participant.project_integration_user_participant?.id;
          submittalParticipants.design_reviewer_user = {
            ...submittalParticipants.design_reviewer_user,
            ...participant.project_integration_user_participant
          };
          submittalParticipants.design_reviewer_user.project_users[0].subscription_vendor.name =
            participant.project_integration_user_participant?.project_integration_vendor?.name;
          break;
        case "responsible_contractor":
          submittalParticipants.responsible_contractor =
            participant.project_integration_vendor_participant?.id;
          submittalParticipants.responsible_contractor_vendor = {
            ...submittalParticipants.responsible_contractor_vendor,
            ...participant.project_integration_vendor_participant
          };
          submittalParticipants.responsible_contractor_vendor.name =
            participant.project_integration_vendor_participant?.name;
          submittalParticipants.responsible_contractor_vendor.id =
            participant.project_integration_vendor_participant?.id;
          break;
        default:
        // console.log("participantType not matched: ", participantType);
      }
    }
  );
  const updatedSubmittalDetails = Object.assign(
    JSON.parse(JSON.stringify(submittalDetails)),
    submittalParticipants
  );
  return updatedSubmittalDetails;
};

export const getGQL = (query: string) => {
  return {
    query: gql`query ${query}`,
    sub: gql`subscription ${query}`
  };
};

export const downloadStringAsCSVFile = (data: any, fileName: string) => {
  const blob = new Blob([data], { type: "text/csv" });
  const a = document.createElement("a");
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  a.click();
  a.remove();
};

export const isProductionEnv = () => {
  return window.location.hostname === "app.constructiviq.com";
};

export const isProdDemoEnv = () => {
  return (
    window.location.hostname === "app.constructiviq.com" ||
    window.location.hostname === "demo.constructiviq.com"
  );
};
export const isDemoEnv = () => {
  return window.location.hostname === "demo.constructiviq.com";
};

export const isDevQAEnv = () => {
  return (
    window.location.hostname === "qa.constructiviq.com" ||
    window.location.hostname === "dev.constructiviq.com"
  );
};

export const supersetEnv = () => {
  if (
    window.location.hostname === "app.constructiviq.com" ||
    window.location.hostname === "prodnew.constructiviq.com"
  )
    return "https://dashboard.constructiviq.com";
  return "https://nonprod-dashboard.constructiviq.com";
};

/**
 * This method will return the true if the code is running in development mode.
 * Below are the cases when we can say it is running on developer machine:
 *  - It is running on developer machin using `npm start` command.
 *  - It is running on developer machin after building the code.
 *  - Applocation can be loaded using localhost or using IP on the browser on developer machine
 *
 * @returns boolean
 */
export const isLocalhostDevEnv = () => {
  // if it is running from local machine then process will provide the Node Environment as production
  const isDevCodeRunning =
    process.env &&
    process.env.NODE_ENV &&
    process.env.NODE_ENV === "development"; // "production" for build code running after deploying to S3 for environments Dev, QA, Demo, Prod

  return isDevCodeRunning || window.location.hostname === "localhost";
};

/**
 * A simple method to replace the pattern with the values in the given template.
 * data is a hash/dictionary where keys are patterns and values will be used to
 * replace it in the provided template.
 *
 * Example:
 *  template = '/project/#{projectId}/reports/submittals/export?accessToken=#{accessToken}&subscriptionId=#{subscriptionId}'
 *  data = {
 *	 '#{accessToken}': 1234,
 *   '#{subscriptionId}': 'sub-123'
 *  };
 *
 * result : /project/#{projectId}/reports/submittals/export?accessToken=1234&subscriptionId=sub-123
 * @param template
 * @param data
 * @returns
 */
export const renderTemplate = (template: string, data: any) => {
  let renderedTemplate = template;
  // eslint-disable-next-line no-restricted-syntax
  for (const [pattern, value] of Object.entries(data)) {
    renderedTemplate = renderedTemplate.replaceAll(pattern, `${value}`);
  }
  return renderedTemplate;
};

/**
 * Method to get the web app url
 * @returns
 */
export const getTheAppUrl = () => {
  return `${window.location.protocol}//${window.location.hostname}${
    window.location.port ? `:${window.location.port}` : ""
  }`;
};

export const getInitials = (fullName: string) => {
  const letterSplit = (fullName && fullName.split(" ")) || ["", ""];
  let letters = letterSplit.map((word: string) => word[0]).join("");
  if (letters.length > 3) {
    letters = letters.slice(0, 3);
  }
  return letters;
};

export const downloadFileFromS3andOpenInNewTab = async (
  url: string,
  type: string,
  fileName?: string
) => {
  const downloadResp = await noAuthClient.get(url, {
    responseType: "blob"
  });
  if (downloadResp.data) {
    const file = new Blob([downloadResp.data], { type });
    const fileURL = URL.createObjectURL(file);

    if (type === "application/pdf") {
      // since we need to open pdfs in new tab instead of download.
      window.open(fileURL);
    } else {
      const a = document.createElement("a");
      a.href = fileURL;
      if (fileName) {
        a.download = fileName;
      }
      document.body.appendChild(a);
      a.click();
    }
  }
  return downloadResp;
};

export const getACCAuthErrorMessage = (errorObj: any) => {
  return errorObj.error_description || errorObj.error || "";
};

export const fileSelectValidator = (
  files: File[],
  config: { allowedFileTypes: string | string[]; maxSizeInMB: number }
) => {
  const { allowedFileTypes, maxSizeInMB } = config;

  const errorMessages: string[] = [];
  let allFilesPassed = true;

  const passedFiles: File[] = [];
  const failedFiles: { file: File; errorMessage: string }[] = [];

  const validateFile = (file: File) => {
    const fileType = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();
    if (allowedFileTypes !== "*" && !allowedFileTypes.includes(fileType)) {
      return `File type ${fileType} is not allowed for ${file.name}`;
    }

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > maxSizeInMB) {
      return `File ${file.name} exceeds the maximum size limit of ${maxSizeInMB}MB`;
    }
    return "";
  };

  files.forEach((file: File) => {
    const validationErrorMsg: string = validateFile(file);

    if (validationErrorMsg) {
      allFilesPassed = false;
      errorMessages.push(validationErrorMsg);
      failedFiles.push({
        file,
        errorMessage: validationErrorMsg
      });
    } else {
      passedFiles.push(file);
    }
  });

  const validationResponse = {
    allFilesPassed,
    errorMessages,
    passedFiles,
    failedFiles
  };

  return validationResponse;
};

export const subscriptionTokenExpiryInterceptor = (config: any) => {
  const token = getAccessToken();

  if (token !== null) {
    const expirationTime = (getUser() as any).exp * 1000 - 60000;
    if (Date.now() >= expirationTime) {
      logout();
    }
  }

  return config;
};

/**
 * Utility method to check if network is available or not.
 * Note: This utility will declair offline only if it is able to detect using
 * the browser native api otherwise it will return true. To make it better if
 * browser api is not there then we can use http call and track if network is
 * there or not but it is not inscope of this function.
 * @returns
 *
 * reference https://developer.mozilla.org/en-US/docs/Web/API/Navigator/onLine
 */
export const isNetworkAvailable = () => {
  return typeof navigator !== "undefined" &&
    typeof navigator.onLine === "boolean"
    ? navigator.onLine
    : true;
};

export const getProjectUserSources = (params: any) => {
  const userSourceInfos = params?.subscription_user?.user_source_infos;
  const allSources: string[] = [];
  if (userSourceInfos?.length) {
    userSourceInfos
      .map((usi: any) => usi.full_source_name)
      ?.reduce((acc: string[], item: string) => {
        if (!acc.includes(item)) {
          acc.push(item);
        }
        return acc;
      }, allSources);
  }
  return allSources;
};

export const getProjectVendorSources = (data: any) => {
  const allSources: string[] = [];
  if (data?.subscription_vendor?.vendor_source_infos) {
    data?.subscription_vendor?.vendor_source_infos
      .map((vsi: any) => vsi.full_source_name)
      ?.reduce((acc: string[], item: string) => {
        if (!acc.includes(item)) {
          acc.push(item);
        }
        return acc;
      }, allSources);
  }
  return allSources;
};

export const getSubscriptionUserSources = (params: any) => {
  const allSources: string[] = [];
  if (params?.user_source_infos) {
    params?.user_source_infos
      .map((usi: any) => usi.full_source_name)
      ?.reduce((acc: string[], item: string) => {
        if (!acc.includes(item)) {
          acc.push(item);
        }
        return acc;
      }, allSources);
  }
  return allSources;
};

export const getSubscriptionVendorSources = (data: any) => {
  const allSources: string[] = [];
  if (data?.vendor_source_infos) {
    data?.vendor_source_infos
      .map((vsi: any) => vsi.full_source_name)
      ?.reduce((acc: string[], item: string) => {
        if (!acc.includes(item)) {
          acc.push(item);
        }
        return acc;
      }, allSources);
  }
  return allSources;
};

export const updatePropertyAtPath = (
  obj: any,
  path: string,
  value: any
): any => {
  const keys = path.split(".");
  const lastKey = keys.pop()!;

  const newObj = structuredClone(obj);

  keys.reduce((acc, key) => {
    if (!acc[key]) {
      acc[key] = {};
    }
    return acc[key];
  }, newObj)[lastKey] = value;
  return newObj;
};

export const validatorForFieldValue = async (
  field: string,
  value: any,
  featureType: string,
  token: any,
  errorMsg: string
) => {
  const fieldMap: any = {};
  fieldMap[field] = { _eq: value };

  const where = {
    _and: [{ feature: { feature: { _eq: featureType } } }, fieldMap]
  };

  return new Promise((resolve, reject) => {
    if (!value) {
      resolve(true);
      return;
    }
    const checkDuplicate = async () => {
      const response = await getGQLQueryData(
        token,
        CHECK_FEATURE_FIELD_VALUE_EXISTS,
        { where }
      );

      if (response?.data?.data?.feature_instance?.length > 0) {
        reject(errorMsg);
      }
      resolve(true);
    };
    checkDuplicate();
  });
};

export const getDaysText = (value: number | undefined | null) => {
  if (value === null || value === undefined) return "";

  if (value === 1) return "Day";
  return "Days";
};

export enum ImportStatuses {
  PROCESSING = "PROCESSING",
  PROCESSED = "PROCESSED",
  FAILED = "FAILED",
  NA = "NA"
}

export type InviteAccUserAction = {
  isFromProjectUser: boolean;
  userData: any;
  setDrawerOpen: Function;
  showDrawerOpen: boolean;
  modelTitle: string;
};

export const sortAlphanumeric = (arr: any[], key: string) => {
  const sort = [...arr];
  return sort.sort((a, b) => {
    const alphanumericCompare = (strChar: any) => {
      const str = `${strChar}`;
      return (
        str && str?.split(/(\d+)/)?.map((x: any) => (Number.isNaN(+x) ? x : +x))
      );
    };
    const aParts = alphanumericCompare(a[key]);
    const bParts = alphanumericCompare(b[key]);
    for (let i = 0; i < Math.min(aParts?.length, bParts?.length); i = 1 + i) {
      if (aParts[i] !== bParts[i]) {
        return aParts[i] < bParts[i] ? -1 : 1;
      }
    }
    return (aParts?.length || 0) - (bParts?.length || 0);
  });
};

export const sortLinkedItems = (items: any[], key: string) => {
  const orderedItems = sortAlphanumeric(items, key);

  // Filtered out Void Submittals here
  const voidedEntities = orderedItems.filter(
    (entity: any) =>
      entity?.workflow_status &&
      entity?.workflow_status === ESubmittalStatus.VOID
  );

  let sortedItems: any[] = [];
  const priorityOrder: any = { HIGH: 1, MEDIUM: 2, LOW: 3, null: 4 };

  if (
    key === "material_sequence_id" ||
    key === "number" ||
    key === "auto_inc_value"
  ) {
    sortedItems = orderedItems.sort(
      (a, b) => priorityOrder[a?.risk_level] - priorityOrder[b?.risk_level]
    );
  } else {
    // Added sorting for non voided entities
    const nonVoidedEntities = orderedItems.filter(
      (entity: any) =>
        entity?.workflow_status &&
        entity?.workflow_status !== ESubmittalStatus.VOID
    );
    sortedItems = nonVoidedEntities
      .sort((a, b) =>
        a?.workflow_status && a?.workflow_status > b?.workflow_status ? -1 : 1
      )
      .sort(
        (a, b) => priorityOrder[a?.risk_level] - priorityOrder[b?.risk_level]
      );
  }

  // Combines voided and non voided entities
  return [...voidedEntities, ...sortedItems];
};

export const updateGridSelectionOnFilter = (event: any, gridRef: any) => {
  const filtersApplied = event?.api?.getFilterModel();
  if (filtersApplied) {
    Object.keys(filtersApplied).forEach((key: any) => {
      if (filtersApplied[key]?.values) {
        gridRef?.current?.api?.deselectAll();
      }
    });
  }
};

export const getScheduleFileExtension = (
  selectedSchedule: any,
  fileExtention: string
) => {
  if (!selectedSchedule?.import_log?.source_values_s3_prefix) return false;
  const fileExt = selectedSchedule?.import_log?.source_values_s3_prefix
    .split(".")
    .pop();
  if (fileExtention === fileExt) return true;
  return false;
};

export const escapeCsvValue = (value: any): string => {
  if (value == null) return '""';
  return `"${String(value)
    .replace(/"/g, '""')
    .replace(/[\r\n]+/g, " ")}"`;
};

type TreeNode = {
  path: string;
  name: string;
  children: TreeNode[];
};
export function buildHierarchy(paths: string[]): TreeNode[] {
  const rootMap = new Map<string, TreeNode>();

  function addPath(path: string) {
    const parts = path.split(">").map((p) => p.trim());
    let currentMap = rootMap;
    let currentKey = "";
    let parent: TreeNode | null = null;

    for (let i = 0; i < parts.length; i += 1) {
      const part = parts[i];
      currentKey = currentKey ? `${currentKey} > ${part}` : part;

      if (!currentMap.has(part)) {
        const newNode: TreeNode = {
          path: currentKey,
          name: part,
          children: []
        };
        currentMap.set(part, newNode);
        if (parent) {
          parent.children.push(newNode);
        }
      }

      parent = currentMap.get(part)!;
      currentMap = new Map(parent.children.map((child) => [child.name, child]));
    }
  }

  paths.forEach((path) => {
    addPath(path);
  });

  return Array.from(rootMap.values());
}

interface OriginalNode {
  id: string;
  name: string;
  order: number;
  path: string;
  children: OriginalNode[];
  [key: string]: any;
}

interface MappedNode {
  id: string;
  name: string;
  order: number;
  path: string;
  key?: string;
  title: string;
  value?: string;
  children: MappedNode[];
  [key: string]: any;
}

export const formatLBSTreeData = (
  nodes: OriginalNode[],
  mapNode: (node: OriginalNode) => MappedNode
): MappedNode[] => {
  return nodes.map((node) => {
    const newNode = mapNode(node);
    if (node.children && node.children.length > 0) {
      newNode.children = formatLBSTreeData(
        node.children.sort((a, b) => a.name.localeCompare(b.name)),
        mapNode
      );
    }

    return newNode;
  });
};

type Item = { id: string; path: string };

export const areArrayObjectsEqualByKey = (
  arrays: Item[][],
  key: string
): boolean => {
  if (arrays.length === 0) return true;

  const getSortedIds = (arr: Item[]) => _.sortBy(_.map(arr, key));

  const referenceIds = getSortedIds(arrays[0]);

  return arrays.every((arr) => _.isEqual(getSortedIds(arr), referenceIds));
};

export const compareStringArrays = (arr1: string[], arr2: string[]) => {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  const allStrings = _.union(arr1, arr2);
  const sameItems = _.intersection(arr1, arr2);
  const differentItems = allStrings.filter((s) => !set1.has(s) || !set2.has(s));

  const isSame =
    sameItems.length === allStrings.length && arr1.length === arr2.length;

  return {
    isSame,
    sameItems,
    differentItems
  };
};

export const areAllStringArraysEqual = (arrays: string[][]): boolean => {
  if (arrays.length === 0) return true;

  const referenceSet = new Set(arrays[0]);

  return arrays.every((arr) => {
    if (arr.length !== referenceSet.size) return false;

    const currentSet = new Set(arr);
    if (currentSet.size !== referenceSet.size) return false;

    return [...referenceSet].every((uuid) => currentSet.has(uuid));
  });
};

export function getFileNameFromUrl(url: string) {
  return new URL(url).pathname.split("/").pop();
}
export const getFileExtensionFromUrl = (url: string) => {
  const fileName = getFileNameFromUrl(url);
  if (!fileName) return "";
  return fileName.split(".").pop();
};
export const getFileNameWithoutExtension = (fileName: string) => {
  const lastDotIndex = fileName.lastIndexOf(".");
  if (lastDotIndex === -1) return fileName;
  return fileName.substring(0, lastDotIndex);
};
